import { useEffect, useState } from 'react'
import { Button, Container } from '@mantine/core'
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks'
import { IconPlus } from '@tabler/icons-react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { chamadosApi } from '@/api/chamados'
import { CHAVE_PENDENCIAS_DO_MENU, invalidarPendenciasDoChamado } from '@/api/chamadosPendencias'
import { useAuthStore, useCan } from '@/store/auth'
import { PastasCard } from './PastasCard'
import { FilasCard, OrganizacoesCard } from './CartaoDeFiltro'
import { ListaDaPasta } from './ListaDaPasta'
import { ehPasta, pastaInicial, rotuloDaPasta, temResponsavel } from './pastas'
import { comOrdem, lerOrdem, ordemNaPasta, type Ordem } from './ordem'
import { useTituloDaTela } from '@/store/tituloDaTela'
import { useAlturaAteOFim } from '@/utils/alturaDaTela'
import type { EstadoDaLeitura } from '@/components/chamados/leitura/vizinhos'
import classes from './Entrada.module.css'

const POR_PAGINA = 50

/**
 * A Entrada no modelo Mailbox (spec 2026-10-19 §3): coluna com Abrir chamado, Pastas e
 * Organizações (e Filas, nas pastas de fila); à direita o cartão da pasta aberta. A pasta, a
 * organização e a fila moram na URL (`?pasta=&org=&fila=`): dá para compartilhar, e a pasta não muda sozinha quando a contagem chega
 * depois (§4). Contadores e menu saem da MESMA consulta (`/me/inbox/counts`, §4.1).
 */
export function Entrada() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const viewerUserId = useAuthStore((s) => s.user?.id)
  const can = useCan()

  const pastaDaUrl = params.get('pasta')
  const orgDaUrl = params.get('org')
  const org = orgDaUrl != null && /^\d+$/.test(orgDaUrl) ? Number(orgDaUrl) : null
  const filaDaUrl = params.get('fila')
  const fila = filaDaUrl != null && /^\d+$/.test(filaDaUrl) ? Number(filaDaUrl) : null
  // A ordem da lista (spec 2026-10-21): na URL, como pasta, organização e fila. Responsável só
  // vale nas pastas que têm a coluna (spec 2026-10-22 §3.2).
  const comResponsavel = temResponsavel(ehPasta(pastaDaUrl) ? pastaDaUrl : null)
  const ordem = ordemNaPasta(lerOrdem(params), comResponsavel)
  const ordemColuna = ordem?.coluna ?? null
  const ordemDirecao = ordem?.direcao ?? null

  const [busca, setBusca] = useState('')
  const [q] = useDebouncedValue(busca.trim(), 300)
  const [offset, setOffset] = useState(0)

  // A mesma chave do número do menu: uma consulta só para as duas coisas (§4.1).
  const contagem = useQuery({
    queryKey: CHAVE_PENDENCIAS_DO_MENU,
    queryFn: async () => (await chamadosApi.getInboxCounts()).data,
    staleTime: 60_000,
  })

  // Sem pasta na URL, a pasta inicial sai da contagem UMA vez e vai para a URL (replace).
  // Contagem que falha cai em Atribuídos a mim, em vez de deixar a tela parada.
  const semPasta = !ehPasta(pastaDaUrl)
  useEffect(() => {
    if (!semPasta) return
    if (contagem.data === undefined && !contagem.isError) return
    const next = new URLSearchParams(params)
    next.set('pasta', pastaInicial(contagem.data))
    setParams(next, { replace: true })
  }, [semPasta, contagem.data, contagem.isError, params, setParams])

  const pasta = ehPasta(pastaDaUrl) ? pastaDaUrl : null

  useEffect(() => { setOffset(0) }, [pasta, q, org, fila, ordemColuna, ordemDirecao])

  // O topo diz a pasta aberta, e a faixa "Entrada / O que precisa da sua atenção" sai
  // (pedido de 21/10/2026): o nome da pasta já está no topo e no cartão da lista.
  useTituloDaTela(pasta ? rotuloDaPasta(pasta) : 'Entrada', { semFaixa: true })

  // No computador (≥ 1200 px, onde as duas colunas ficam lado a lado) as pastas e a lista
  // rolam cada uma por conta própria, como na Base de Conhecimento (pedido de 21/10/2026).
  // Abaixo disso a coluna vira barra de pastas em cima da lista e a página rola inteira.
  const desktop = useMediaQuery('(min-width: 75em)')
  const colunas = useAlturaAteOFim<HTMLDivElement>(desktop === true)
  const rolagemPropria = colunas.altura != null

  const lista = useQuery({
    // Prefixo `chamados-inbox`: `invalidarPendenciasDoChamado` já recarrega a Entrada.
    queryKey: ['chamados-inbox', 'pasta', pasta, q, org, fila, ordemColuna, ordemDirecao, offset],
    queryFn: async () => (await chamadosApi.getPasta({
      pasta: pasta!, q: q || undefined, org, fila, ordem: ordemColuna, direcao: ordemDirecao,
      limit: POR_PAGINA, offset,
    })).data,
    enabled: pasta != null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

  const hrefDo = (chave: 'org' | 'fila') => (id: number | null) => {
    const next = new URLSearchParams(params)
    if (id == null) next.delete(chave)
    else next.set(chave, String(id))
    return `?${next.toString()}`
  }
  const hrefDaOrg = hrefDo('org')
  const hrefDaFila = hrefDo('fila')

  // `push`, não `replace`: o Voltar do navegador desfaz a ordenação.
  const ordenar = (nova: Ordem | null) => setParams(comOrdem(params, nova))

  // A origem vai no `state` ao abrir: o detalhe anda por ela com ↑ ↓ (spec 2026-10-21 §4), na
  // mesma ordem da lista (spec 2026-10-21 ordenar por coluna §2.3).
  const estadoAoAbrir: EstadoDaLeitura | undefined = pasta
    ? { entrada: { pasta, q: q || null, org, fila, ordem: ordemColuna, dir: ordemDirecao } }
    : undefined

  const atualizar = () => {
    void lista.refetch()
    void contagem.refetch()
  }

  return (
    <Container fluid pt="md" pb={rolagemPropria ? 0 : "md"}>
      <div
        ref={colunas.ref}
        data-testid="entrada-colunas"
        className={classes.disposicao}
        style={rolagemPropria ? { height: colunas.altura!, alignItems: 'stretch' } : undefined}
      >
        <div
          data-testid="entrada-pastas-rolagem"
          className={classes.coluna}
          style={rolagemPropria ? { height: '100%', overflowY: 'auto' } : undefined}
        >
          {/* Ação primária: o gradiente da marca vem do tema (DS §6.1). */}
          <Button fullWidth leftSection={<IconPlus size={16} />} onClick={() => navigate('/chamados/novo')}>
            Abrir chamado
          </Button>
          <PastasCard
            ativa={pasta ?? 'assigned'}
            counts={contagem.data}
            contagemFalhou={contagem.isError && contagem.data === undefined}
          />
          {lista.data && (
            <FilasCard
              filas={lista.data.filas ?? []}
              minhasFilasTotal={lista.data.minhas_filas_total ?? 0}
              ativa={fila}
              hrefDe={hrefDaFila}
              onEscolher={(f) => navigate(hrefDaFila(f))}
              rolagemPropria={rolagemPropria}
            />
          )}
          {lista.data && (
            <OrganizacoesCard
              organizacoes={lista.data.organizacoes}
              ativa={org}
              hrefDe={hrefDaOrg}
              onEscolher={(o) => navigate(hrefDaOrg(o))}
              rolagemPropria={rolagemPropria}
            />
          )}
        </div>

        <ListaDaPasta
          titulo={pasta ? rotuloDaPasta(pasta) : 'Entrada'}
          consulta={lista}
          aviso={contagem.data?.aviso_feriado_local ?? null}
          busca={busca}
          onBusca={setBusca}
          pagina={{ offset, limit: POR_PAGINA, onMudar: setOffset }}
          viewerUserId={viewerUserId}
          podeAtribuir={can('chamados', 'assign')}
          onAtualizar={atualizar}
          onDepoisDoLote={() => invalidarPendenciasDoChamado(queryClient)}
          onAbrir={(id) => navigate(`/chamados/${id}`, { state: estadoAoAbrir })}
          estadoAoAbrir={estadoAoAbrir}
          ordem={ordem}
          onOrdem={ordenar}
          comResponsavel={comResponsavel}
          rolagemPropria={rolagemPropria}
        />
      </div>
    </Container>
  )
}
